import Cookies from "js-cookie";
import CookieKeys from "@/model/constants/cookieKeys";

/**
 * Oturum yönetimi — tek yerden:
 *  - Token / yetki / kullanıcı bilgisi localStorage'da tutulur; eski çıkış kodu bunların bir kısmını
 *    cookie'den silmeye çalıştığı için yetkiler ve kullanıcı bilgisi tarayıcıda kalıyor, çıkıştan
 *    sonra sayfalar açılabiliyordu. Burada hepsi silinir.
 *  - Çıkışta token sunucuda da iptal edilir (api/auth/logout).
 *  - Giriş sayfasına location.replace ile gidilir: geri tuşu eski sayfaya dönmez ve Vuex durumu sıfırlanır.
 */

const AUTH_LOCAL_KEYS = [CookieKeys.AUTH_KEY, CookieKeys.PERMISSIONS, CookieKeys.USER_DETAILS, "userInfo"];
const CART_LOCAL_KEYS = ["selfservice_carts", "selfservice_payment_session"];
const SESSION_KEYS = ["selfservice_locked", "brew_last_unknown_pos_at"];

/** Giriş yapmadan açılabilen sayfalar (route name). Müşteri ekranı API çağırmaz, yalnızca kasanın
 * BroadcastChannel ile gönderdiği sepeti gösterir. */
export const PUBLIC_ROUTE_NAMES = [
  "page-login",
  "bis-login",
  "document-login",
  "page-forgot-password",
  "page-reset-password",
  "page-error-404",
  "page-error-500",
  "page-not-authorized",
  "page-maintenance",
  "CustomerScreen"
];

let loggingOut = false;

function safe(fn) {
  try {
    fn();
  } catch (e) {
    // depolama erişilemiyorsa sessizce geç
  }
}

export function getAuthToken() {
  let token = null;
  safe(() => {
    token = localStorage.getItem(CookieKeys.AUTH_KEY);
  });
  return token || Cookies.get(CookieKeys.AUTH_KEY) || null;
}

/** Token var ve süresi dolmamış mı (JWT exp). İmza sunucuda doğrulanır; burada yalnızca açıkça
 * geçersiz durumlar (yok / bozuk / süresi dolmuş) yakalanır. */
export function hasValidToken() {
  const token = getAuthToken();
  if (!token) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload && payload.exp && payload.exp * 1000 <= Date.now()) return false;
    return true;
  } catch (e) {
    return false;
  }
}

/** Tarayıcıdaki oturum bilgisini siler. keepCart: oturum düşse bile kasadaki sepet/ödeme kaybolmasın. */
export function clearSession({ keepCart = false } = {}) {
  const localKeys = keepCart ? AUTH_LOCAL_KEYS : AUTH_LOCAL_KEYS.concat(CART_LOCAL_KEYS);
  localKeys.forEach((k) => safe(() => localStorage.removeItem(k)));
  [CookieKeys.AUTH_KEY, CookieKeys.PERMISSIONS, CookieKeys.USER_DETAILS].forEach((k) => safe(() => Cookies.remove(k)));
  SESSION_KEYS.forEach((k) => safe(() => sessionStorage.removeItem(k)));
}

function revokeOnServer(token) {
  const base = String(process.env.VUE_APP_ROOT_API || "").replace(/\/+$/, "");
  if (!base || !token) return Promise.resolve();
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 3000) : null;
  return fetch(`${base}/auth/logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    keepalive: true,
    signal: controller ? controller.signal : undefined
  })
    .catch(() => {})
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
}

/**
 * Oturumu kapatır ve giriş sayfasına gider.
 *  notifyServer: token sunucuda iptal edilsin (oturum zaten reddedildiyse gerekmez).
 *  keepCart: kasadaki sepet/ödeme bilgisi silinmesin (ör. oturum ödeme sırasında düştüyse).
 *  redirectTo: girişten sonra dönülecek sayfa.
 */
export async function logout({ notifyServer = true, keepCart = false, redirectTo = null } = {}) {
  if (loggingOut) return;
  loggingOut = true;
  const token = getAuthToken();
  if (notifyServer) {
    await revokeOnServer(token);
  }
  clearSession({ keepCart });
  const query = redirectTo && redirectTo !== "/pages/login" ? `?to=${encodeURIComponent(redirectTo)}` : "";
  window.location.replace(`/pages/login${query}`);
}

const SESSION_CHECK_INTERVAL_MS = 60000;
let sessionWatchTimer = null;

/**
 * Açık sayfada oturumu düzenli kontrol eder. Kasa gibi uzun süre işlem yapılmadan açık kalan ekranlar,
 * kullanıcı silinince / pasife alınınca / başka sekmede çıkış yapılınca ilk API çağrısını beklemeden
 * giriş sayfasına düşer. İstek fetch ile atılır (axios yükleme göstergesi her dakika yanıp sönmesin).
 *  isPublic(): şu anki sayfa giriş gerektirmiyor mu
 *  isBusy(): ödeme sürüyor mu — sürüyorsa bu tur atlanır, ödeme sonucu kaybolmasın
 *  currentPath(): girişten sonra dönülecek sayfa
 * İnternet/sunucu hatasında bir şey yapılmaz; yalnızca 401 oturumu kapatır.
 */
export function startSessionWatch({ isPublic = () => false, isBusy = () => false, currentPath = () => null } = {}) {
  if (sessionWatchTimer) return;
  const check = async () => {
    if (loggingOut || isPublic()) return;
    if (isBusy()) return;
    const expire = () => logout({ notifyServer: false, keepCart: true, redirectTo: currentPath() });
    const token = getAuthToken();
    if (!token || !hasValidToken()) {
      expire();
      return;
    }
    const base = String(process.env.VUE_APP_ROOT_API || "").replace(/\/+$/, "");
    if (!base) return;
    try {
      const res = await fetch(`${base}/auth/check`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
      // Yanıt gelene kadar başka sekmede yeniden giriş yapılmış olabilir: yalnızca aynı token reddedildiyse kapat.
      if (res.status === 401 && getAuthToken() === token && !isBusy()) expire();
    } catch (e) {
      // çevrimdışı / sunucuya ulaşılamıyor: oturuma dokunma
    }
  };
  sessionWatchTimer = setInterval(check, SESSION_CHECK_INTERVAL_MS);
  safe(() =>
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    })
  );
}
